import { ArrowForward } from '@mui/icons-material';
import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useAccountLoyaltySummaryQuery } from 'api/loyalty-domain/loyalty-domain';
import { useGetCustomerQuery } from 'api/user-domain/user-domain';
import CustomerCard from 'components/customer/customer-card/customer-card.component';
import CustomerDetailCardActions from 'pages/crm/customers/customer-details/customer-card/customer-detail-card-actions.component';
import CustomerRecentTransactionDataGrid from 'pages/crm/customers/customer-details/customer-overview/customer-recent-transaction-data-grid/customer-recent-transaction-data-grid.component';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import IncDrawer, { IncDrawerType } from '../inc-drawer/inc-drawer.component';
import UtilityDrawerBody from '../utility-drawer/utility-drawer-body/utility-drawer-body.component';
import CustomerQuickDetailsOverviewCards from './customer-quick-details-overview-cards/customer-quick-details-overview-cards.component';

export type CustomerQuickDetailsDrawerDataType = { customerId: string };

type CustomerQuickDetailsDrawerProps =
  IncDrawerType<CustomerQuickDetailsDrawerDataType>;

const CustomerQuickDetailsDrawer = ({
  close,
  data,
  ...drawerRest
}: CustomerQuickDetailsDrawerProps) => {
  const { t } = useTranslation();

  const { currentData: customerData, isFetching } = useGetCustomerQuery(
    data?.customerId ?? skipToken,
  );
  const { currentData: currentLoyaltyPoints } = useAccountLoyaltySummaryQuery(
    data?.customerId
      ? {
          userId: data?.customerId,
        }
      : skipToken,
  );

  const cancel = () => {
    close();
  };

  return (
    <IncDrawer
      isLoading={isFetching}
      close={cancel}
      data={data}
      data-testid="customerDetailsQuickDrawer.Drawer"
      title={t('drawer.customerQuickDetailsDrawer.title')}
      {...drawerRest}
    >
      <UtilityDrawerBody>
        <Stack spacing={2}>
          <Box display={'flex'} justifyContent={'flex-end'}>
            <MuiLink
              sx={{ display: 'flex', alignItems: 'flex-end' }}
              component={Link}
              to={`/crm/customers/${customerData?.userId}`}
              onClick={() => {
                close();
              }}
            >
              <Typography variant="body2">
                {t('drawer.customerQuickDetailsDrawer.fulldetails.label')}
              </Typography>
              <ArrowForward sx={{ mr: 1, ml: 1 }} fontSize="small" />
            </MuiLink>
          </Box>
          {customerData && (
            <CustomerCard
              customer={customerData}
              cardHeaderProps={{
                action: (
                  <CustomerDetailCardActions
                    customer={customerData}
                    onDelete={() => {
                      cancel();
                    }}
                  />
                ),
              }}
            />
          )}
          <CustomerQuickDetailsOverviewCards
            customerData={customerData}
            confirmedPoints={currentLoyaltyPoints?.confirmedPoints}
          />
          <Box>
            {customerData?.userId && (
              <CustomerRecentTransactionDataGrid
                customer={customerData}
                detailDrawer={true}
                close={close}
                count={4}
              />
            )}
          </Box>
        </Stack>
      </UtilityDrawerBody>
    </IncDrawer>
  );
};

export default CustomerQuickDetailsDrawer;
